import { useState, FormEvent } from "react";
import { useResolveIssue } from "src/hooks/useResolveIssue";
import { IssueResolutionFormValidator } from "src/validation";
import { APIError, ValidationErrors } from "src/errors";
import { Issue, ResolveIssueFormData, ResolveIssueFormDataKey, ResolveIssueFormAction, BaseModel } from "src/types";
import { ResolveIssueForm } from "src/components/forms";
import { ResolveIssueModals } from "src/components/modals";
import { LoadingState } from "src/components";

interface ResolveIssueProps<T extends BaseModel & Issue> {
  id: string;
  resolveIssue: (id: string, resolutionDetails: string) => Promise<T | undefined>;
}

const ResolveIssue = <T extends BaseModel & Issue>({ id, resolveIssue }: ResolveIssueProps<T>) => {
  const [formData, setFormData] = useState<ResolveIssueFormData>({ id: id, resolutionDetails: "" });

  let [action] = useState<ResolveIssueFormAction>("RESOLVE_CONFIRMATION");

  const [loadingMessage, setLoadingMessage] = useState<string>();

  const [validationErrors, setValidationErrors] = useState<ValidationErrors<ResolveIssueFormDataKey>>();
  const [formLvlErrorMessage, setFormLvlErrorMessage] = useState<string>("");

  const [modalType, setModalType] = useState<keyof typeof ResolveIssueModals>();

  const [confirmationCallback, setConfirmationCallback] = useState<() => void>(() => null);

  const { mutateAsync: resolveIssueMutation, isPending } = useResolveIssue<T>(resolveIssue, {
    onSuccess: () => {
      setModalType(undefined);
    },
    onError: (error) => {
      setModalType(undefined);
      if (error instanceof APIError && error.type === "CLIENT_ERROR") {
        setFormLvlErrorMessage(error.message);
      } else {
        setModalType("Error");
      }
    }
  });

  const handleInputChange = (
    field: ResolveIssueFormDataKey,
    value: ResolveIssueFormDataKey[keyof ResolveIssueFormDataKey]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prevErrors) => prevErrors?.clearValidationError(field));
    setFormLvlErrorMessage("");
  };

  const resolve = async () => {
    setLoadingMessage("Resolving...");
    await resolveIssueMutation({ id, resolutionDetails: formData.resolutionDetails });
  };

  const resolveConfirmation = async () => {
    setConfirmationCallback(() => async () => {
      action = "RESOLVE";
      await handleSubmit();
    });
    setModalType("Warning");
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();

    const validationErrors = IssueResolutionFormValidator.validateIssueResolutionFormData(formData);

    if (validationErrors?.length) {
      setValidationErrors(validationErrors);
      return;
    }

    try {
      switch (action) {
        case "RESOLVE_CONFIRMATION":
          await resolveConfirmation();
          break;
        case "RESOLVE":
          await resolve();
          break;
      }
    } finally {
      setLoadingMessage(undefined);
    }
  };

  const renderFormContent = () => {
    const isLoading = isPending || loadingMessage;

    if (isLoading) {
      return <LoadingState loadingMessage={loadingMessage} />;
    }

    let ModalComponent;

    if (modalType) {
      const Modal = ResolveIssueModals[modalType];
      ModalComponent = (
        <Modal
          id={formData.id}
          onCancel={() => setModalType(undefined)}
          onConfirm={confirmationCallback}
          onRetry={handleSubmit}
        />
      );
    }

    return (
      <div className="space-y-6">
        {ModalComponent && ModalComponent}
        <ResolveIssueForm
          formData={formData}
          validationErrors={validationErrors}
          formLvlErrorMessage={formLvlErrorMessage}
          isFormComplete={!!formData.resolutionDetails}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
        />
      </div>
    );
  };

  return renderFormContent();
};

export { ResolveIssue };
